/**
 * The structure lines a store source's Test (dry run) prints under its fetch
 * summary: how many parents / variants / singles the import would write, how
 * many of those docs already exist (would update) vs not (would create), and
 * the no-native-grouping hint.
 *
 * Counted with the same helpers as the bulk-import preview (importStructure.ts),
 * over every product the Test fetched. The preview can still differ: there the
 * curator picks product types, unticks rows, and accepts or rejects each group
 * the rule proposes. So the rule's groups are shown both ways — as the store
 * has it, and with every proposed group accepted.
 */
import type { VariantGroupingRule } from '../../types';
import {
  describeCounts,
  lacksNativeGrouping,
  NO_NATIVE_GROUPING_HINT,
  planImportStructure,
  splitCreateUpdate,
  type StructureRow,
} from '../CuratedWatches/importStructure';
import { storeDocIdForEntry, variantGroupOf, type StoreIdentifiedEntry } from '../CuratedWatches/storeDocIds';
import { proposeRuleGroups, ruleIsActive } from '../CuratedWatches/variantGroupingRule';

export interface TestStructureInput {
  entries: ReadonlyArray<StoreIdentifiedEntry & { reference?: string }>;
  rule?: VariantGroupingRule;
  /** Doc ids that already exist under the brand; null when the lookup failed. */
  existingDocIds: ReadonlySet<string> | null;
  /** The source flattens the store's own groups (Shopify "ignore parent-child"). */
  nativeGroupingOff?: boolean;
}

/** Every doc id the import could write for these entries: rows + store parents. */
export function docIdsToCheck(entries: ReadonlyArray<StoreIdentifiedEntry>): string[] {
  return [
    ...new Set(
      entries.flatMap((e) => [storeDocIdForEntry(e), variantGroupOf(e)?.parentDocId]).filter((id): id is string => !!id),
    ),
  ];
}

export function testStructureLines({ entries, rule, existingDocIds, nativeGroupingOff }: TestStructureInput): string[] {
  const rows: StructureRow[] = entries.map((entry) => ({ entry, included: true }));
  if (rows.length === 0) return [];
  const lines: string[] = [];

  const native = planImportStructure(rows);
  lines.push(`Would import: ${describeCounts(native.counts)}`);

  const proposed = ruleIsActive(rule)
    ? proposeRuleGroups(
        entries.map((e) => ({ reference: e.reference ?? '', included: true, storeGrouped: !!variantGroupOf(e) })),
        rule,
      )
    : [];
  const final =
    proposed.length > 0
      ? planImportStructure(
          rows,
          proposed.map((g) => ({ key: g.key, parentIndex: g.parentIndex, variantIndices: g.members.filter((m) => m !== g.parentIndex) })),
        )
      : native;
  if (proposed.length > 0) {
    lines.push(
      `Your grouping rule proposes ${proposed.length} group${proposed.length === 1 ? '' : 's'}. ` +
        `If you accept them all in the preview: ${describeCounts(final.counts)}`,
    );
  }

  if (existingDocIds) {
    const split = splitCreateUpdate(rows, final, storeDocIdForEntry, existingDocIds);
    lines.push(
      `Would create ${split.wouldCreate} · would update ${split.wouldUpdate} (already in Watch Keeper)` +
        (split.wouldUpdate > 0 ? ` — updates only fill empty fields; your edits are kept` : ''),
    );
  } else {
    lines.push('Could not check which references already exist.');
  }

  if (lacksNativeGrouping(rows) && proposed.length === 0) {
    lines.push(
      nativeGroupingOff
        ? 'Parent-child grouping is switched off for this source ("Ignore Shopify parent-child relationships"), so every product imports on its own.'
        : NO_NATIVE_GROUPING_HINT,
    );
  }
  return lines;
}
